import { ApiService } from '../ApiService';
import type { Account, JournalEntry } from '../../../types/accounting';
import type { 
  LedgerSearchParams, 
  AccountMovementSearchParams, 
  TrialBalanceParams, 
  ExportParams,
  TrialBalanceEntry,
  TrialBalanceResponse,
  AccountBalance,
  BusinessUnitFilterBase
} from '../../../types/filters';
import type { PaginatedResponse } from '../types';
import { CurrencyCode } from '../../../config/currency';

/**
 * Interface pour les mouvements de compte conformément à la documentation
 */
export interface AccountMovement {
  id: string;
  date: string;
  journalType: 'sales' | 'purchases' | 'bank' | 'cash' | 'general';
  reference: string;
  description: string;
  debit: number;
  credit: number;
  runningBalance?: number;
}

/**
 * Interface pour le grand livre d'un compte
 */
export interface AccountLedger {
  account: {
    id: string;
    code: string;
    name: string;
    type: string;
    class?: string;
  };
  openingBalance: number;
  closingBalance: number;
  totalDebit: number;
  totalCredit: number;
  movements: AccountMovement[];
}

/**
 * Interface pour une entrée du grand livre général
 */
export interface GeneralLedgerEntry {
  id: string;
  date: string;
  journalType: 'sales' | 'purchases' | 'bank' | 'cash' | 'general';
  reference: string;
  description: string;
  debit: number;
  credit: number;
  runningBalance?: number;
}

/**
 * Interface pour un compte dans le grand livre général
 */
export interface GeneralLedgerAccount {
  account: {
    id: string;
    code: string;
    name: string;
    type: 'asset' | 'liability' | 'equity' | 'revenue' | 'expense';
    class?: string;
  };
  entries: GeneralLedgerEntry[];
  totals: {
    debit: number;
    credit: number;
    openingBalance: number;
    closingBalance: number;
    normalizedBalance: number;
  };
  entryCount: number;
}

/**
 * Interface pour le grand livre général (réponse complète)
 */
export interface GeneralLedgerResponse {
  period: {
    startDate: string;
    endDate: string;
    fiscalYear: string;
  };
  companyId: string;
  accounts: GeneralLedgerAccount[];
  grandTotals: {
    totalDebit: number;
    totalCredit: number;
    accountCount: number;
    entryCount: number;
  };
  generatedAt: string;
}

/**
 * Interface pour le grand livre (compatible avec l'ancienne version)
 */
export interface GeneralLedger {
  accounts: AccountLedger[];
  summary: {
    totalDebit: number;
    totalCredit: number;
    accountCount: number;
  };
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Résultat de recherche dans le grand livre
 */
export interface LedgerSearchResult {
  results: Array<{
    entryId: string;
    date: string;
    reference: string;
    description: string;
    account: {
      id: string;
      code: string;
      name: string;
    };
    debit: number;
    credit: number;
    journalType: 'sales' | 'purchases' | 'bank' | 'cash' | 'general';
  }>;
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Interface pour la réponse des mouvements d'un compte
 * Conformément à la documentation API
 */
export interface AccountMovementsResponse {
  account: {
    id: string;
    code: string;
    name: string;
    type: string;
  };
  openingBalance: number;
  closingBalance: number;
  totalDebit: number;
  totalCredit: number;
  movements: AccountMovement[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Interface pour la réponse brute du backend pour la balance
 * Le backend renvoie 'entries' au lieu de 'items'
 */
interface BackendTrialBalanceResponse {
  entries?: TrialBalanceEntry[];
  items?: TrialBalanceEntry[];
  totals?: {
    totalDebit: number;
    totalCredit: number;
    totalBalance?: number;
    totalDebitBalance?: number;
    totalCreditBalance?: number;
  };
  currency?: string;
  period?: {
    asOfDate?: string;
    fiscalYear?: string;
    startDate?: string;
    endDate?: string;
  };
  generatedAt?: string;
  companyId?: string;
  controls?: {
    isBalanced: boolean;
    balancesMatch: boolean;
    difference: number;
  };
  accountCount?: number;
}

/**
 * Adaptateur pour normaliser la réponse de la balance du backend vers le format attendu
 * Backend peut renvoyer 'entries' au lieu de 'items'
 */
function normalizeTrialBalanceResponse(backendData: BackendTrialBalanceResponse): TrialBalanceResponse {
  // Utiliser 'items' s'il existe, sinon 'entries', sinon tableau vide
  const items = backendData.items || backendData.entries || [];
  
  const totals = backendData.totals || { totalDebit: 0, totalCredit: 0 };
  
  return {
    period: {
      asOfDate: backendData.period?.asOfDate || backendData.generatedAt?.split('T')[0] || new Date().toISOString().split('T')[0],
      fiscalYear: backendData.period?.fiscalYear || ''
    },
    companyId: backendData.companyId || '',
    items: Array.isArray(items) ? items : [],
    totals: {
      totalDebit: totals.totalDebit || 0,
      totalCredit: totals.totalCredit || 0,
      totalDebitBalance: totals.totalDebitBalance || 0,
      totalCreditBalance: totals.totalCreditBalance || 0
    },
    controls: backendData.controls || {
      isBalanced: totals.totalDebit === totals.totalCredit,
      balancesMatch: true,
      difference: (totals.totalDebit || 0) - (totals.totalCredit || 0)
    },
    accountCount: backendData.accountCount || items.length,
    generatedAt: backendData.generatedAt || new Date().toISOString()
  };
}

export const ledgerApi = {
  /**
   * Récupérer le solde d'un compte
   * @see GET /ledger/accounts/:accountId/balance
   */
  getAccountBalance: (accountId: string, params?: { 
    date?: string;
    currency?: CurrencyCode;
  } & BusinessUnitFilterBase) => 
    ApiService.get<{ success: boolean; data: AccountBalance }>(`/ledger/accounts/${accountId}/balance`, params),

  /**
   * Récupérer les mouvements d'un compte
   * @see GET /ledger/accounts/:accountId/movements
   */
  getAccountMovements: (accountId: string, params?: AccountMovementSearchParams) => 
    ApiService.get<{ success: boolean; data: AccountMovementsResponse }>(`/ledger/accounts/${accountId}/movements`, params),

  /**
   * Récupérer la balance de vérification (balance générale)
   * @see GET /ledger/trial-balance
   */
  getTrialBalance: async (params?: TrialBalanceParams): Promise<{ success: boolean; data: TrialBalanceResponse }> => {
    const response = await ApiService.get<BackendTrialBalanceResponse>('/ledger/trial-balance', params);
    
    if (response.success && response.data) {
      return {
        success: true,
        data: normalizeTrialBalanceResponse(response.data)
      };
    }
    
    // En cas d'erreur, retourner une structure vide mais valide
    return {
      success: response.success,
      data: normalizeTrialBalanceResponse({})
    };
  },

  /**
   * Exporter la balance
   * @see GET /ledger/export-balance
   */
  exportBalance: (params: ExportParams) =>
    ApiService.get<Blob>('/ledger/export-balance', params),

  /**
   * Export général du grand livre
   * @see GET /ledger/export
   */
  exportLedger: (params: ExportParams & { 
    exportType?: 'trial-balance' | 'account-movements';
    accountId?: string;
    accountIds?: string[];
    startDate?: string;
    endDate?: string;
  }) => 
    ApiService.get<Blob>('/ledger/export', params),

  /**
   * Recherche dans le grand livre
   * @see GET /ledger/search
   */
  search: (query: string, params?: LedgerSearchParams) =>
    ApiService.get<{ success: boolean; data: LedgerSearchResult }>('/ledger/search', { query, ...params }),

  /**
   * Récupérer le grand livre général avec tous les comptes et mouvements
   * @see GET /ledger/general-ledger
   */
  getGeneralLedger: (params?: {
    fiscalYearId?: string;
    startDate?: string;
    endDate?: string;
    accountCodes?: string[];      // Filtrer par codes de comptes spécifiques
    withRunningBalance?: boolean; // Calculer le solde progressif après chaque écriture
    includeDetails?: boolean;
    page?: number;
    pageSize?: number;
  } & BusinessUnitFilterBase) =>
    ApiService.get<{ success: boolean; data: GeneralLedgerResponse }>('/ledger/general-ledger', params)
};